import { useCallback, useEffect, useMemo, useState, type MouseEvent } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import {
  Box, Stack, Typography, Button, Alert, TextField, MenuItem, Menu, Paper, IconButton, Tooltip,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, CircularProgress, Breadcrumbs, Link,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/EditOutlined";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { BRAND } from "../styles/theme";
import {
  Collector, Movement, getCollector, getCollectorMovements, addMovement, addMovementTag, changeTransferStatus,
} from "../services/collectors";
import { StatusChip, fmtDateTime } from "../components/collectors/chips";
import ConfirmDialog from "../components/collectors/ConfirmDialog";

const OPERATION_TYPES = [
  { value: "transfer",   label: "Transferencia" },
  { value: "settlement", label: "Liquidación" },
];
const OPERATION_LABELS: Record<string, string> = Object.fromEntries(OPERATION_TYPES.map((o) => [o.value, o.label]));
const translateOperation = (op: string) => OPERATION_LABELS[op] ?? op;

// v1 sólo coloreaba pending/failed (el resto quedaba sin traducir ni color);
// acá se completan completed/sent con el mismo StatusChip que usa el resto de collectors.
const STATUS_KIND: Record<string, "ok" | "bad" | "warn" | "info" | "neutral"> = {
  completed: "ok", sent: "info", failed: "bad", pending: "warn",
};

// v1 sólo permitía cambiar el status vía long-press cuando estaba en uno de
// estos 3 valores (no tiene equivalente en web) — acá es un menú siempre visible.
const CHANGEABLE_STATUSES = ["completed", "failed", "sent"];

function fmtAmount(n: number): string {
  return n.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * CP-435: acepta el monto en formato argentino ("28.214.153,65", "28214153,65") o con
 * punto decimal ("28214153.65"), hasta 2 decimales y con signo. Un solo punto seguido de
 * 3 dígitos ("1.500") es separador de miles. Devuelve null si no es un monto válido.
 */
function parseAmount(raw: string): number | null {
  const s = raw.trim().replace(/[\s$]/g, "");
  let norm: string;
  if (/^-?\d+(\.\d{1,2})?$/.test(s)) norm = s;
  else if (/^-?(\d{1,3}(\.\d{3})+|\d+)(,\d{1,2})?$/.test(s)) norm = s.replace(/\./g, "").replace(",", ".");
  else return null;
  const n = Number(norm);
  return Number.isFinite(n) ? n : null;
}

/** Ledger de settlements de un collector (v1: `MenuCollectorAddMovement`). */
export default function CollectorMovements() {
  const { id = "" } = useParams();

  const [collector, setCollector] = useState<Collector | null>(null);
  const [currency, setCurrency]   = useState("");
  const [available, setAvailable] = useState(0);
  const [rows, setRows]           = useState<Movement[]>([]);
  const [loading, setLoading]     = useState(true);
  const [err, setErr]             = useState<string | null>(null);

  const [q, setQ]           = useState("");
  const [status, setStatus] = useState("all");
  const [ok, setOk]         = useState<string | null>(null);

  // Add Movement: campos inline (como en v1) + diálogo de confirmación.
  // CP-423: amount/description vivían acá mismo, en el componente que también
  // tiene `rows` (la lista completa de movimientos, sin paginar) — cada tecla
  // re-renderizaba toda la tabla. Ahora el formulario es un componente aparte
  // con su propio estado; acá solo queda el valor "pendiente" que se fija una
  // sola vez, al tocar "Add Movement" (no en cada tecla).
  const [pendingAdd, setPendingAdd]     = useState<{ amount: number; description: string; operationType: string } | null>(null);
  const [formResetKey, setFormResetKey] = useState(0);
  const [addErr, setAddErr]             = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen]   = useState(false);
  const [newAvailable, setNewAvailable] = useState(0);
  const [checkingBalance, setCheckingBalance] = useState(false);

  // Edit Tag: ícono por fila → diálogo con un TextField libre.
  const [tagRow, setTagRow]     = useState<Movement | null>(null);
  const [tagValue, setTagValue] = useState("");

  // Change status: menú anclado al ícono de la fila (reemplaza el long-press de v1).
  const [statusAnchor, setStatusAnchor] = useState<HTMLElement | null>(null);
  const [statusRow, setStatusRow]       = useState<Movement | null>(null);

  const load = useCallback(async (c: Collector) => {
    const summary = await getCollectorMovements(c);
    setCurrency(summary.currency); setAvailable(summary.availableAmount); setRows(summary.movements);
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setErr(null);
      try {
        const c = await getCollector(id);
        if (!c) { if (alive) setErr(`Collector "${id}" not found.`); return; }
        if (!alive) return;
        setCollector(c);
        await load(c);
      } catch (e: any) { if (alive) setErr(String(e?.message ?? e)); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [id, load]);

  /** Mismas validaciones que la página de v1, antes de abrir el confirm. */
  async function onAddMovementClick(values: { amount: string; description: string; operationType: string }) {
    setAddErr(null); setOk(null);
    if (!values.operationType || !values.amount.trim() || !values.description.trim()) {
      setAddErr("Please make sure to complete all fields: amount, description and operation type.");
      return;
    }
    const n = parseAmount(values.amount);
    if (n === null) { setAddErr("Please check if the amount is a valid number (e.g. 28.214.153,65)."); return; }
    if (!collector) return;
    // CP-352: `available` puede haber quedado desactualizado (otra operación, liquidación, etc.
    // entre la última carga y este click) — refrescarlo antes de calcular el saldo futuro que
    // se muestra en el confirm, para no mostrar un número que ya no es real.
    setCheckingBalance(true);
    let freshAvailable: number;
    try {
      const summary = await getCollectorMovements(collector);
      setCurrency(summary.currency); setAvailable(summary.availableAmount); setRows(summary.movements);
      freshAvailable = summary.availableAmount;
    } catch (e: any) { setAddErr(`Couldn't refresh the current balance: ${String(e?.message ?? e)}`); return; }
    finally { setCheckingBalance(false); }
    const na = freshAvailable + n;
    if (na < 0) { setAddErr("The new available cannot be negative."); return; }
    setPendingAdd({ ...values, amount: n });
    setNewAvailable(na);
    setConfirmOpen(true);
  }

  async function onConfirmAddMovement() {
    if (!collector || !pendingAdd) return;
    await addMovement(collector, { currency, amount: pendingAdd.amount, operationType: pendingAdd.operationType, description: pendingAdd.description });
    setConfirmOpen(false);
    setPendingAdd(null);
    setFormResetKey((k) => k + 1);
    setOk("A new movement was successfully added.");
    await load(collector);
  }

  function onEditTagClick(m: Movement) {
    setTagRow(m);
    setTagValue(m.tag);
  }

  async function onConfirmEditTag() {
    if (!collector || !tagRow) return;
    await addMovementTag(collector.id, tagRow, tagValue.trim());
    setTagRow(null);
    await load(collector);
  }

  function onStatusIconClick(e: MouseEvent<HTMLElement>, m: Movement) {
    setStatusAnchor(e.currentTarget);
    setStatusRow(m);
  }

  async function onChooseStatus(newStatus: string) {
    const m = statusRow;
    setStatusAnchor(null); setStatusRow(null);
    if (!collector || !m || newStatus === m.status) return;
    setErr(null); setOk(null);
    try {
      await changeTransferStatus(collector.id, m, newStatus);
      setOk("Status updated.");
      await load(collector);
    } catch (e: any) { setErr(String(e?.message ?? e)); }
  }

  const statusOptions = useMemo(() => Array.from(new Set(rows.map((m) => m.status))).sort(), [rows]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return rows.filter((m) => {
      if (status !== "all" && m.status !== status) return false;
      if (!s) return true;
      return [m.tag, m.description, translateOperation(m.operationType), m.status, m.currency]
        .some((v) => v.toLowerCase().includes(s));
    });
  }, [rows, q, status]);

  if (loading) return <Box sx={{ p: 3, display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>;

  if (!collector) {
    return (
      <Box sx={{ p: 3 }}>
        <Button component={RouterLink} to="/collector-operation" startIcon={<ArrowBackIcon />} sx={{ mb: 2 }}>
          Back to Collector Operation
        </Button>
        <Alert severity="error">{err ?? `Collector "${id}" not found.`}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Button component={RouterLink} to="/collector-operation" startIcon={<ArrowBackIcon />} size="small" sx={{ mb: 1 }}>
        Back to Collector Operation
      </Button>
      <Breadcrumbs sx={{ mb: 1, fontSize: 13 }}>
        <Link component={RouterLink} to="/collector-operation" underline="hover" color="text.secondary">Collector Operation</Link>
        <Typography variant="body2" color="text.primary">{collector.id}</Typography>
      </Breadcrumbs>

      <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }} sx={{ mb: 0.5 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Collector {collector.id}</Typography>
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontWeight: 700 }}>Available: {currency} {fmtAmount(available)}</Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
        Only movements within the last 10 days are displayed.
      </Typography>

      {err && <Alert severity="error" onClose={() => setErr(null)} sx={{ mb: 2 }}>{err}</Alert>}
      {ok  && <Alert severity="success" onClose={() => setOk(null)} sx={{ mb: 2 }}>{ok}</Alert>}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 15, mb: 1.5 }}>Add movement</Typography>
        <AddMovementForm key={formResetKey} onSubmit={onAddMovementClick} disabled={checkingBalance} />
        {addErr && <Alert severity="error" onClose={() => setAddErr(null)} sx={{ mt: 1.5 }}>{addErr}</Alert>}
      </Paper>

      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ mb: 2 }}>
        <TextField size="small" label="Search" placeholder="Tag, description, operation, status…" value={q}
          onChange={(e) => setQ(e.target.value)} sx={{ width: 320 }} />
        <TextField size="small" select label="Status" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ width: 180 }}>
          <MenuItem value="all">All</MenuItem>
          {statusOptions.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </TextField>
      </Stack>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead sx={{ bgcolor: BRAND.primaryLight }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Business Date</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Operation Date/Time</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Tag</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Operation Type</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Currency</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">Amount</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">Available Balance</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: "text.secondary" }}>
                {rows.length === 0 ? "No movements in the last 10 days." : "No movements match."}
              </TableCell></TableRow>
            ) : filtered.map((m, i) => (
              <TableRow key={`${m.pk}-${m.sk}-${i}`} hover>
                <TableCell>{m.businessDate || "—"}</TableCell>
                <TableCell>{fmtDateTime(m.dateTime)}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.25} alignItems="center">
                    <span>{m.tag || <span style={{ color: "#888" }}>—</span>}</span>
                    <Tooltip title="Edit tag">
                      <IconButton size="small" onClick={() => onEditTagClick(m)}><EditIcon sx={{ fontSize: 15 }} /></IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
                <TableCell>{translateOperation(m.operationType)}</TableCell>
                <TableCell>{m.description}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.25} alignItems="center">
                    <StatusChip kind={STATUS_KIND[m.status] ?? "neutral"} label={m.status || "—"} />
                    {CHANGEABLE_STATUSES.includes(m.status) && (
                      <Tooltip title="Change status">
                        <IconButton size="small" onClick={(e) => onStatusIconClick(e, m)}><ArrowDropDownIcon sx={{ fontSize: 18 }} /></IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
                <TableCell>{m.currency}</TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>{fmtAmount(m.amount)}</TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>{fmtAmount(m.availableBalance)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Stack direction="row" alignItems="center" sx={{ px: 2, py: 1, borderTop: 1, borderColor: "divider" }}>
          <Typography variant="caption" color="text.secondary">{filtered.length} of {rows.length} movements</Typography>
        </Stack>
      </TableContainer>

      <ConfirmDialog open={confirmOpen} onClose={() => setConfirmOpen(false)} onConfirm={onConfirmAddMovement}
        title="Add movement" okLabel="Add Movement" busyLabel="Adding…">
        <Typography variant="body2">Your movement amount is: <b>{currency} {fmtAmount(pendingAdd?.amount ?? 0)}</b></Typography>
        <Typography variant="body2">Description: <b>{pendingAdd?.description}</b></Typography>
        <Typography variant="body2">Operation Type: <b>{translateOperation(pendingAdd?.operationType ?? "")}</b></Typography>
        <Typography sx={{ mt: 1.5, fontSize: 18 }}>And the new Available will be: <b>{currency} {fmtAmount(newAvailable)}</b></Typography>
      </ConfirmDialog>

      <ConfirmDialog open={!!tagRow} onClose={() => setTagRow(null)} onConfirm={onConfirmEditTag}
        title="Edit tag" okLabel="Ok" busyLabel="Saving…">
        <TextField autoFocus fullWidth size="small" label="Tag" value={tagValue}
          onChange={(e) => setTagValue(e.target.value)} sx={{ mt: 1 }} />
      </ConfirmDialog>

      <Menu anchorEl={statusAnchor} open={!!statusAnchor} onClose={() => { setStatusAnchor(null); setStatusRow(null); }}>
        {CHANGEABLE_STATUSES.map((s) => (
          <MenuItem key={s} onClick={() => onChooseStatus(s)} disabled={s === statusRow?.status}>{s}</MenuItem>
        ))}
      </Menu>
    </Box>
  );
}

/**
 * CP-423: estado propio para Amount/Description/Operation type, separado del
 * componente que tiene la tabla completa de movimientos — así tipear acá no
 * re-renderiza esa tabla. El padre sólo se entera del valor una vez, al
 * tocar "Add Movement" (`onSubmit`), y lo resetea remontando este componente
 * con una `key` distinta tras un alta exitosa.
 */
function AddMovementForm({ onSubmit, disabled }: { onSubmit: (values: { amount: string; description: string; operationType: string }) => void; disabled?: boolean }) {
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [operationType, setOperationType] = useState(OPERATION_TYPES[0].value);

  return (
    <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "flex-start" }}>
      <TextField size="small" select label="Operation type" value={operationType} disabled={disabled}
        onChange={(e) => setOperationType(e.target.value)} sx={{ width: 200 }}>
        {OPERATION_TYPES.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
      </TextField>
      <TextField size="small" label="Amount" value={amount} disabled={disabled} onChange={(e) => setAmount(e.target.value)} sx={{ width: 160 }} />
      <TextField size="small" label="Description" value={description} disabled={disabled} onChange={(e) => setDescription(e.target.value)} sx={{ flex: 1, minWidth: 240 }} />
      <Button variant="contained" startIcon={disabled ? <CircularProgress size={14} color="inherit" /> : <AddIcon />} disabled={disabled}
        onClick={() => onSubmit({ amount, description, operationType })} sx={{ height: 40 }}>
        Add Movement
      </Button>
    </Stack>
  );
}
